import { useState } from "react";
import { Mail, Send, Loader2, Check, AlertCircle } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { trackCta } from "@/lib/analytics";

const FOUNDER_EMAIL = "ek@stoicos.ai";
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/d4ut6c9u_IMG_4907.jpeg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LaurelMark() {
  return (
    <div className="flex items-center justify-center gap-4 mb-10 md:mb-14" aria-hidden>
      <span className="h-px w-24 sm:w-32 bg-gold/50" />
      <svg width="44" height="22" viewBox="0 0 44 22" fill="none" className="text-gold/90">
        <path d="M22 17c-3-4-7-6-12-6 1 4 5 7 12 7s11-3 12-7c-5 0-9 2-12 6Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" fill="none"/>
        <path d="M22 4v13" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      </svg>
      <span className="h-px w-24 sm:w-32 bg-gold/50" />
    </div>
  );
}

export default function ShareYourThoughts() {
  const { t, lang } = useT();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | savedOnly | error
  const [errorMsg, setErrorMsg] = useState("");

  useSEO({
    title: lang === "nl"
      ? "Deel je gedachten | StoicOs.ai"
      : "Share Your Thoughts | StoicOs.ai",
    description: lang === "nl"
      ? "Vragen, reflecties, suggesties en feedback zijn welkom. Schrijf rechtstreeks aan de oprichter van StoicOs.ai."
      : "Questions, reflections, suggestions and feedback are welcome. Write directly to the founder of StoicOs.ai.",
    canonical: "https://stoicos.ai/share-your-thoughts",
    jsonLd: [
      webPage({
        name: "Share Your Thoughts",
        description: "A quiet space where guests of StoicOs.ai can share questions, reflections and ideas with the founder.",
        url: "/share-your-thoughts",
        breadcrumbItems: [{ name: "Home", url: "/" }, { name: "Share Your Thoughts", url: "/share-your-thoughts" }],
      }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "Share Your Thoughts", url: "/share-your-thoughts" }]),
    ],
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      setErrorMsg(t("share.form.errEmpty"));
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/share-thoughts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim() || null,
          email: form.email.trim() || null,
          message: form.message.trim(),
          lang,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      trackCta("share_thoughts_submit", { has_email: !!form.email, emailed: !!data.emailed });
      setStatus(data.emailed ? "success" : "savedOnly");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(t("share.form.errSend"));
    }
  };

  return (
    <main data-testid="share-thoughts-page" className="relative bg-ink">
      {/* ========== HERO IMAGE — full-width, no overlays ========== */}
      <section className="relative w-full pt-20 md:pt-24" data-testid="share-hero">
        <div className="relative w-full overflow-hidden">
          <img
            src={HERO_IMG}
            alt={lang === "nl"
              ? "Een perkamentrol met de Latijnse inscriptie ‘Seneca Lucilio Salutem’, een brandende olielamp en een marmeren buste van Seneca op een donkere studeertafel."
              : "A parchment scroll inscribed ‘Seneca Lucilio Salutem’, a lit oil lamp and a marble bust of Seneca on a dark study table."}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="share-hero-image"
            className="block w-full h-auto"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 md:h-32"
               aria-hidden
               style={{ background: "linear-gradient(to bottom, transparent, rgba(5,5,5,0.95) 70%, rgba(5,5,5,1))" }} />
        </div>
      </section>

      {/* ========== CONTENT ========== */}
      <section className="relative w-full px-6 sm:px-10 pt-16 md:pt-20 pb-24 md:pb-32">
        <article className="max-w-2xl mx-auto text-center">
          <LaurelMark />

          <h1
            data-testid="share-title"
            className="font-serif text-gold uppercase tracking-[0.06em] text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.0]"
          >
            {t("share.headline")}
          </h1>

          <p
            data-testid="share-lede"
            className="mt-7 md:mt-9 font-reading text-bone/90 text-lg md:text-xl leading-[1.6]"
          >
            {t("share.lede")}
          </p>

          <div className="mx-auto h-px w-20 bg-gold/45 my-10 md:my-12" />

          <div className="space-y-6 font-reading text-bone/85 text-base md:text-lg leading-[1.85] text-left sm:text-center">
            <p data-testid="share-p1">{t("share.p1")}</p>
            <p data-testid="share-p2">{t("share.p2")}</p>
            <p data-testid="share-p3">{t("share.p3")}</p>
          </div>

          {/* Email line */}
          <a
            href={`mailto:${FOUNDER_EMAIL}`}
            data-testid="share-email-link"
            onClick={() => trackCta("share_thoughts_email", { destination: FOUNDER_EMAIL })}
            className="mt-12 md:mt-14 inline-flex items-center gap-4 text-gold hover:text-gold-hover transition-colors group"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-gold/60 group-hover:border-gold transition-colors">
              <Mail size={18} strokeWidth={1.5} />
            </span>
            <span className="font-serif text-2xl sm:text-3xl">{FOUNDER_EMAIL}</span>
          </a>

          <p
            data-testid="share-reassurance"
            className="mt-10 md:mt-12 font-reading text-bone/65 text-base md:text-lg leading-[1.85] italic"
          >
            {t("share.reassurance")}
          </p>
        </article>

        {/* ========== CONTACT FORM ========== */}
        <article className="max-w-2xl mx-auto mt-20 md:mt-24" data-testid="share-form-wrap">
          <LaurelMark />
          <h2 className="text-center font-serif text-gold uppercase tracking-[0.18em] text-xl md:text-2xl mb-3">
            {t("share.form.title")}
          </h2>
          <p className="text-center font-reading text-bone/70 text-sm md:text-base leading-[1.7] mb-4">
            {t("share.form.subtitle")}
          </p>
          <p
            data-testid="share-form-delivery"
            className="text-center font-reading text-gold/85 text-[13px] md:text-sm leading-[1.7] mb-10"
          >
            {t("share.form.deliveryNotice")}{" "}
            <a
              href={`mailto:${FOUNDER_EMAIL}`}
              className="underline decoration-gold/40 hover:decoration-gold text-gold"
            >
              {FOUNDER_EMAIL}
            </a>
          </p>

          {status === "success" ? (
            <div
              data-testid="share-form-success"
              className="border border-gold/40 bg-[#0a0907] p-8 md:p-10 text-center"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold/60 mb-5">
                <Check size={20} className="text-gold" strokeWidth={1.6} />
              </span>
              <p className="font-serif text-gold text-xl md:text-2xl">{t("share.form.successTitle")}</p>
              <p className="mt-3 font-reading text-bone/75 text-base leading-[1.75]">{t("share.form.successBody")}</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-7 text-[11px] tracking-[0.3em] uppercase text-gold/85 hover:text-gold"
              >
                {t("share.form.writeAgain")}
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="share-form"
              className="space-y-5 md:space-y-6"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-2">
                    {t("share.form.nameLabel")}
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="share-form-name"
                    autoComplete="name"
                    placeholder={t("share.form.namePlaceholder")}
                    className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none font-reading text-bone py-3 placeholder-bone/30 transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-2">
                    {t("share.form.emailLabel")}
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="share-form-email"
                    autoComplete="email"
                    placeholder={t("share.form.emailPlaceholder")}
                    className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none font-reading text-bone py-3 placeholder-bone/30 transition-colors"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-2">
                  {t("share.form.messageLabel")}
                </span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="share-form-message"
                  rows={7}
                  required
                  placeholder={t("share.form.messagePlaceholder")}
                  className="w-full bg-[#0a0907]/60 border border-gold/30 focus:border-gold outline-none font-reading text-bone p-4 leading-[1.7] placeholder-bone/30 transition-colors resize-y min-h-[180px]"
                />
              </label>

              {status === "error" && (
                <div
                  data-testid="share-form-error"
                  className="flex items-start gap-2.5 text-[13px] text-rose-300/90"
                >
                  <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[11px] text-bone/45 leading-relaxed">
                  {t("share.form.privacy")}
                </p>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="share-form-submit"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold hover:bg-gold-hover text-ink text-[11px] tracking-[0.32em] uppercase font-semibold whitespace-nowrap transition-all duration-500 disabled:opacity-60 disabled:cursor-wait"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 size={14} strokeWidth={2} className="animate-spin" />
                      {t("share.form.sending")}
                    </>
                  ) : (
                    <>
                      {t("share.form.send")}
                      <Send size={14} strokeWidth={2} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </article>

        {/* Founder signature */}
        <div className="max-w-2xl mx-auto mt-20 md:mt-24 text-center">
          <div className="mx-auto h-px w-24 bg-gold/40 mb-8" />
          <p
            className="font-serif italic text-gold text-2xl md:text-3xl"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            {t("share.signature.name")}
          </p>
          <p className="mt-2 text-[11px] tracking-[0.32em] uppercase text-bone/55">
            {t("share.signature.role")}
          </p>
        </div>
      </section>
    </main>
  );
}
